import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { badgesFor, healthOf } from '../lib/format';

const Bars = ({ level, tone }) => (
  <span className={`bars lv-${tone}`}>
    {[0, 1, 2, 3].map((index) => (
      <i key={index} className={index < 4 - level ? 'on' : ''} />
    ))}
  </span>
);

/**
 * The ranked release list for a title, and the only route into playback.
 *
 * The backend already merges and ranks across indexers, so the first row is
 * the recommended one and gets the accent treatment. Everything shown here is
 * read off the release itself — nothing is inferred to fill a column.
 */
const Sources = ({ title, season, episode, playToken = 0 }) => {
  const [state, setState] = useState({ loading: true, results: [], meta: null, error: '' });
  const [starting, setStarting] = useState(null);
  const [playError, setPlayError] = useState('');
  const navigate = useNavigate();
  const playedToken = useRef(0);

  useEffect(() => {
    if (!title) return undefined;

    let cancelled = false;
    setState({ loading: true, results: [], meta: null, error: '' });

    const params = new URLSearchParams({ type: title.type, limit: '25' });
    if (title.imdbId) params.set('imdbId', title.imdbId);
    if (title.year) params.set('year', String(title.year));
    if (season != null) params.set('season', String(season));
    if (episode != null) params.set('episode', String(episode));

    axios
      .get(`/api/search/${encodeURIComponent(title.name)}?${params}`)
      .then(({ data }) => {
        if (!cancelled) {
          setState({ loading: false, results: data.data || [], meta: data.meta || null, error: '' });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          results: [],
          meta: null,
          error: err.response?.data?.error || 'Could not reach the indexers'
        });
      });

    return () => {
      cancelled = true;
    };
  }, [title, season, episode]);

  const play = useCallback(
    async (result) => {
      setStarting(result.infoHash);
      setPlayError('');

      const label = {
        // Identifies the entry in watch history; for a debrid link there is no
        // torrent on this server, but the infohash still names the release
        torrentId: result.infoHash,
        title: title.name,
        subtitle:
          season != null && episode != null
            ? `S${season} E${episode} · ${result.resolution || 'source'}`
            : result.resolution || 'source',
        backdrop: title.backdrop,
        // Captions are matched on the IMDB id, and for a series on the exact
        // episode. The player is reached by URL and has no catalog lookup of
        // its own, so what it isn't told here it cannot find out.
        imdbId: title.imdbId || null,
        type: title.type,
        season: season ?? null,
        episode: episode ?? null
      };

      try {
        // A cached debrid link plays over plain HTTP with no swarm to join, so
        // it's worth trying first; anything else falls through to WebTorrent.
        if (state.meta?.debrid && result.cached === true) {
          const res = await axios.post('/api/debrid/resolve', {
            magnet: result.magnet,
            season,
            episode
          });
          const playUrl = res.data.data?.playUrl;
          if (playUrl) {
            navigate(`/watch?src=${encodeURIComponent(playUrl)}`, { state: label });
            return;
          }
        }

        const res = await axios.post('/api/torrents/add', {
          magnet: result.magnet,
          season,
          episode
        });
        const { torrentId, selectedFileId } = res.data.data;

        // A pack with no obvious episode match needs a human to pick the file
        if (selectedFileId == null) {
          navigate(`/torrent/${torrentId}`, { state: label });
          return;
        }
        navigate(`/player/${torrentId}/${selectedFileId}`, { state: label });
      } catch (err) {
        setPlayError(err.response?.data?.error || 'Could not start that source');
      } finally {
        setStarting(null);
      }
    },
    [navigate, title, season, episode, state.meta]
  );

  // The big Play button promises playback, not a list. It bumps playToken, and
  // the top-ranked release starts as soon as the search settles.
  useEffect(() => {
    if (!playToken || playToken === playedToken.current) return;
    if (state.loading || !state.results.length) return;
    playedToken.current = playToken;
    play(state.results[0]);
  }, [playToken, state.loading, state.results, play]);

  const responded = state.meta?.sources?.filter((source) => source.ok).length;
  const total = state.meta?.sources?.length;

  return (
    <section className="section">
      <div className="section-head">
        <h3 className="section-title">
          Sources
          {season != null && episode != null && (
            <span className="section-scope">for S{season} E{episode}</span>
          )}
        </h3>
        <span className="section-note">
          {state.loading
            ? 'asking every indexer…'
            : [
                `${state.results.length} available`,
                total ? `${responded}/${total} indexers responded` : null
              ]
                .filter(Boolean)
                .join(' · ')}
        </span>
      </div>

      {playError && <div className="alert">{playError}</div>}
      {state.error && <div className="alert">{state.error}</div>}

      {state.loading ? (
        <div className="sources">
          {Array.from({ length: 4 }, (_, index) => (
            <div key={index} className="skeleton" style={{ height: 58 }} />
          ))}
        </div>
      ) : state.results.length === 0 ? (
        <div className="empty">
          No release matches this{episode != null ? ' episode' : ' title'} on the indexers we can reach.
        </div>
      ) : (
        <div className="sources">
          {state.results.map((result, index) => {
            const health = healthOf(result);
            return (
              <button
                type="button"
                key={result.infoHash}
                className={`source${index === 0 ? ' is-best' : ''}`}
                onClick={() => play(result)}
                disabled={starting !== null}
              >
                <span className="source-res">{result.resolution || 'SD'}</span>
                <span className="source-badges">
                  {badgesFor(result).map((badge) => (
                    <span
                      key={badge.label}
                      className={`badge${badge.kind === 'fmt' ? ' badge--fmt' : ''}`}
                    >
                      {badge.label}
                    </span>
                  ))}
                </span>
                <span className="source-health">
                  <Bars level={health.level} tone={health.tone} />
                  <span className={`health-${health.tone}`}>{health.label}</span>
                  <span style={{ color: 'var(--fg-6)' }}>{result.seeders}</span>
                </span>
                <span className="source-size">{result.size}</span>
                <span className="source-note">
                  {index === 0 ? 'Best available · ' : ''}
                  {result.isSeasonPack ? 'season pack · ' : ''}
                  {result.sources.join(', ')}
                  {result.group ? ` · ${result.group}` : ''}
                </span>
                <span className={`btn btn--sm${index === 0 ? ' btn--play' : ''}`}>
                  <span className="play-tri play-tri--sm" />
                  {starting === result.infoHash ? 'Starting…' : 'Play'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default Sources;
